import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Globe, ImagePlus, Loader2, MapPin, Palette, RotateCcw, X } from "lucide-react";
import { API, errMsg } from "../lib/api";

const NAV = { retail: ["Shop", "Collections", "About", "Contact"], restaurant: ["Menu", "Reserve", "About", "Contact"], salon_spa: ["Services", "Book", "Gallery", "Contact"],
  clinic_healthcare: ["Treatments", "Doctors", "Book", "Contact"], real_estate: ["Properties", "Projects", "About", "Contact"], education: ["Courses", "Admissions", "About", "Contact"] };
const PRESETS = [["#0b3d2e", "#c9a13b", "Emerald & gold"], ["#1e3a8a", "#f59e0b", "Navy & amber"], ["#7f1d1d", "#f5d0a9", "Maroon & sand"], ["#111827", "#22d3ee", "Charcoal & aqua"],
  ["#4c1d95", "#f472b6", "Plum & rose"], ["#065f46", "#fb923c", "Forest & orange"], ["#0f766e", "#fde68a", "Teal & butter"], ["#9a3412", "#1f2937", "Terracotta & slate"]];
const DEFAULT = { primary: "#0b3d2e", accent: "#c9a13b" };

const onColour = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45 ? "#0f172a" : "#ffffff";
};

const hex = (r, g, b) => `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;

function suggestFromImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const s = 64, cv = document.createElement("canvas");
        cv.width = s; cv.height = s;
        const ctx = cv.getContext("2d");
        ctx.drawImage(img, 0, 0, s, s);
        const d = ctx.getImageData(0, 0, s, s).data, buckets = {};
        for (let i = 0; i < d.length; i += 4) {
          const [r, g, b, a] = [d[i], d[i + 1], d[i + 2], d[i + 3]];
          const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
          if (a < 128 || mx - mn < 40 || mx > 245 && mn > 200 || mx < 25) continue;
          const k = `${r >> 5}-${g >> 5}-${b >> 5}`;
          const bk = buckets[k] || (buckets[k] = { n: 0, r: 0, g: 0, b: 0 });
          bk.n++; bk.r += r; bk.g += g; bk.b += b;
        }
        const cols = Object.values(buckets).sort((x, y) => y.n - x.n).map((x) => [Math.round(x.r / x.n), Math.round(x.g / x.n), Math.round(x.b / x.n)]);
        if (!cols.length) return resolve(null);
        const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
        const second = cols.find((c) => dist(c, cols[0]) > 90);
        const lum = (c) => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
        let [p, a] = second ? [cols[0], second] : [cols[0], null];
        if (a && lum(a) < lum(p)) [p, a] = [a, p];
        const darker = (c) => c.map((v) => Math.round(v * 0.55));
        const primary = !a && lum(p) > 150 ? darker(p) : p;
        const accent = a || (lum(p) > 150 ? p : [201, 161, 59]);
        resolve({ primary: hex(...(a ? p : primary)), accent: hex(...accent) });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function LogoSuggestion({ logo, current, apply }) {
  const [s, setS] = useState(null);
  useEffect(() => { setS(null); if (logo) suggestFromImage(logo).then(setS); }, [logo]);
  if (!s) return null;
  const used = current?.primary === s.primary && current?.accent === s.accent;
  return (
    <div className="flex items-center gap-2 rounded-xl bg-[#f4efe2] px-3 py-2 chat-in" data-testid="logo-colour-suggestion">
      <span className="w-7 h-7 rounded-full shadow shrink-0" style={{ background: `linear-gradient(135deg, ${s.primary} 50%, ${s.accent} 50%)` }} />
      <span className="text-[11px] text-slate-600 flex-1">Matched to your logo <span className="font-mono text-slate-400">{s.primary} · {s.accent}</span></span>
      {used ? <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1" data-testid="logo-colours-applied"><Check className="w-3 h-3" /> In use</span>
        : <button onClick={() => apply(s)} className="text-[11px] font-semibold bg-[#0b3d2e] text-[#f6ecd2] rounded-full px-3 py-1 hover:bg-[#0f4f3c]" data-testid="use-logo-colours-btn">Use these</button>}
    </div>);
}

function ColourPicker({ colours, save, logo }) {
  const [c, setC] = useState(colours || DEFAULT);
  const t = useRef(null);
  useEffect(() => { setC(colours || DEFAULT); }, [colours]);
  const change = (next, now) => { setC(next); clearTimeout(t.current); t.current = setTimeout(() => save(next), now ? 0 : 600); };
  return (
    <div className="px-4 py-3 border-t space-y-2" data-testid="brand-colour-picker">
      <p className="text-[11px] font-semibold text-[#062a20] flex items-center gap-1"><Palette className="w-3.5 h-3.5" /> Your brand colours</p>
      <LogoSuggestion logo={logo} current={colours} apply={(s) => change(s, true)} />
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map(([p, a, label], i) => {
          const on = c.primary === p && c.accent === a;
          return <button key={label} title={label} aria-label={label} onClick={() => change({ primary: p, accent: a }, true)} data-testid={`colour-preset-${i}`}
            className={`relative w-8 h-8 rounded-full overflow-hidden border-2 transition-transform hover:scale-110 ${on ? "border-[#062a20]" : "border-white shadow"}`}>
            <span className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${p} 50%, ${a} 50%)` }} />
            {on && <Check className="absolute inset-0 m-auto w-4 h-4 text-white drop-shadow" />}</button>;
        })}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-slate-600">
        {[["primary", "Main"], ["accent", "Accent"]].map(([k, l]) => (
          <label key={k} className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="color" value={c[k]} onChange={(e) => change({ ...c, [k]: e.target.value })} className="w-7 h-7 rounded border-0 p-0 bg-transparent cursor-pointer" data-testid={`colour-input-${k}`} aria-label={`${l} colour`} />
            {l} <span className="font-mono text-slate-400">{c[k]}</span></label>))}
        {colours && <button onClick={() => save(null)} className="ml-auto inline-flex items-center gap-1 underline" data-testid="colour-reset"><RotateCcw className="w-3 h-3" /> Let AI choose</button>}
      </div>
    </div>);
}

function LogoUpload({ project, api, logo, setLogo }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) { toast.error("Please upload a PNG, JPG or WebP image."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Logos must be 2 MB or smaller."); return; }
    const prev = logo;
    setLogo(URL.createObjectURL(file)); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/projects/${project.id}/assets?kind=logo`, fd);
      setLogo(`${API}/assets/${r.data.id}`);
      toast.success("Logo added — it will be used exactly as supplied.");
    } catch (e) { setLogo(prev); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/projects/${project.id}/brand-logo`); setLogo(null); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="px-4 pt-3 flex items-center gap-2 text-[11px]" data-testid="logo-upload">
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="logo-file-input" />
      <button onClick={() => ref.current?.click()} disabled={busy} className="chip !py-1" data-testid="logo-upload-btn">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}{logo ? "Change logo" : "Upload your logo"}</button>
      {logo && !busy && <button onClick={remove} className="inline-flex items-center gap-1 text-slate-500 underline" data-testid="logo-remove-btn"><X className="w-3 h-3" /> Remove</button>}
      <span className="text-slate-400 ml-auto">PNG, JPG, WebP · 2 MB</span>
    </div>);
}

export default function MiniSitePreview({ project, api }) {
  const p = project.profile || {};
  const [colours, setColours] = useState(project.brand?.colours || null);
  const [logo, setLogo] = useState(project.brand?.logo_asset_id ? `${API}/assets/${project.brand.logo_asset_id}` : null);
  const c = colours || DEFAULT;
  const save = async (next) => {
    setColours(next);
    try { await api.put(`/projects/${project.id}/brand-colours`, next || {}); } catch (e) { toast.error(errMsg(e)); }
  };
  const name = p.business_name?.trim();
  const tagline = p.summary || (p.products_services?.length ? p.products_services.slice(0, 3).join(" · ") : p.industry);
  const nav = NAV[p.business_type] || ["Home", "Services", "About", "Contact"];
  const initials = (name || "Your Business").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const slug = (name || "yourbusiness").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const fg = onColour(c.primary);
  return (
    <div className="rounded-2xl border border-[#0b3d2e]/10 bg-white shadow-sm overflow-hidden" data-testid="mini-site-preview">
      <div className="flex items-center gap-2 px-3 py-2 bg-[#f4efe2] border-b border-[#0b3d2e]/10">
        <span className="flex gap-1">{["#e7b4a6", "#e9d28f", "#a9cfa8"].map((x) => <i key={x} className="w-2.5 h-2.5 rounded-full" style={{ background: x }} />)}</span>
        <span className="flex-1 text-[11px] text-slate-500 bg-white rounded-full px-3 py-0.5 truncate inline-flex items-center gap-1"><Globe className="w-3 h-3" />www.{slug}.com</span>
      </div>
      <div className="px-4 py-3 flex items-center justify-between gap-3 border-b">
        <div className="flex items-center gap-2 min-w-0" key={name || "empty"}>
          {logo ? <img src={logo} alt={`${name || "Business"} logo`} className="h-8 max-w-[96px] w-auto object-contain shrink-0 chat-in" data-testid="mini-site-logo" />
            : <span className="w-8 h-8 rounded-lg grid place-items-center text-xs font-bold shrink-0 chat-in transition-colors" style={{ background: c.primary, color: fg }} data-testid="mini-site-initials">{initials}</span>}
          <span className={`font-semibold truncate chat-in ${name ? "text-[#062a20]" : "text-slate-400 italic"}`} data-testid="mini-site-name">{name || "Your business name"}</span>
        </div>
        <nav className="hidden sm:flex gap-3 text-[11px] text-slate-500">{nav.map((n) => <span key={n}>{n}</span>)}</nav>
      </div>
      <div className="px-4 py-6 transition-colors duration-500" style={{ background: c.primary, color: fg }} data-testid="mini-site-hero">
        <p className="text-lg font-semibold leading-snug chat-in" key={tagline || "t"} data-testid="mini-site-tagline">{tagline || "Your tagline appears here as you chat"}</p>
        {p.location && <p className="mt-2 text-[11px] opacity-80 flex items-center gap-1" data-testid="mini-site-location"><MapPin className="w-3 h-3" />{p.location}</p>}
        <span className="mt-4 inline-block text-[11px] font-semibold rounded-full px-3 py-1 transition-colors" style={{ background: c.accent, color: onColour(c.accent) }}>{p.flags?.sells_online ? "Shop now" : "Get in touch"}</span>
      </div>
      <LogoUpload project={project} api={api} logo={logo} setLogo={setLogo} />
      <ColourPicker colours={colours} save={save} logo={logo} />
      <p className="px-4 pb-2 text-[10px] text-slate-400">{colours ? "Saved — your design draft will use these colours." : "Live sketch only. Pick colours, or let the AI choose for your design draft."}</p>
    </div>
  );
}
